import React from "react";
import { motion } from "framer-motion";
import { FaCode, FaBug, FaRocket } from "react-icons/fa";
import { FiGithub } from "react-icons/fi";
import { Link } from "react-scroll";

const About = () => {
  return (
    <div
      name="about"
      id="about"
      className="w-full min-h-screen pt-24 md:pt-32 transition-colors duration-300
                 bg-gradient-to-b from-gray-50 via-white to-gray-100
                 dark:from-gray-900 dark:via-black dark:to-gray-800"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 py-16 md:py-20 flex flex-col justify-center">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
          className="mb-12 md:mb-16"
        >
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-4 inline-block text-gray-900 dark:text-white">
            About Me
            <span className="block mt-2 h-1 w-24 bg-gradient-to-r from-cyan-500 to-blue-600 rounded"></span>
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-base sm:text-lg max-w-3xl">
            I build reliable software and the pipelines that keep it reliable—combining QA automation,
            CI/CD, and full‑stack development.
          </p>
        </motion.div>

        {/* Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* QA Automation */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45 }}
            className="rounded-xl p-6 md:p-8 border
                       bg-white/70 border-gray-200 shadow-sm
                       dark:bg-gray-800/50 dark:border-gray-700
                       hover:border-cyan-400/60 hover:shadow-cyan-500/10 transition-all duration-300"
          >
            <div className="flex items-center mb-5 md:mb-6">
              <div className="bg-cyan-500/15 dark:bg-cyan-500/20 p-3 rounded-full mr-4">
                <FaBug className="text-cyan-600 dark:text-cyan-400 text-2xl" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                QA Automation
              </h3>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              I design end‑to‑end test suites with{" "}
              <span className="text-cyan-600 dark:text-cyan-400 font-medium">Playwright</span> and{" "}
              <span className="text-cyan-600 dark:text-cyan-400 font-medium">Selenium/Cucumber</span>
              , delivering fast feedback, HTML reports, and actionable evidence (screens, traces).
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              Recent work: SuiteCRM E2E automation with Playwright (TypeScript) and artifacted reports.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs px-2 py-1 rounded border bg-gray-50 text-cyan-700 border-cyan-100 dark:bg-gray-700 dark:text-cyan-300 dark:border-gray-600">
                Playwright
              </span>
              <span className="text-xs px-2 py-1 rounded border bg-gray-50 text-cyan-700 border-cyan-100 dark:bg-gray-700 dark:text-cyan-300 dark:border-gray-600">
                Selenium
              </span>
              <span className="text-xs px-2 py-1 rounded border bg-gray-50 text-cyan-700 border-cyan-100 dark:bg-gray-700 dark:text-cyan-300 dark:border-gray-600">
                Cucumber
              </span>
            </div>
            <a
              href="https://github.com/mohammedyassinAa/pfa-suitecrm-tests"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm font-medium
                         text-cyan-700 hover:text-cyan-800 dark:text-cyan-400 dark:hover:text-cyan-300"
            >
              <FiGithub /> View SuiteCRM tests
            </a>
          </motion.div>

          {/* DevOps & CI/CD */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay: 0.1 }}
            className="rounded-xl p-6 md:p-8 border
                       bg-white/70 border-gray-200 shadow-sm
                       dark:bg-gray-800/50 dark:border-gray-700
                       hover:border-cyan-400/60 hover:shadow-cyan-500/10 transition-all duration-300"
          >
            <div className="flex items-center mb-5 md:mb-6">
              <div className="bg-cyan-500/15 dark:bg-cyan-500/20 p-3 rounded-full mr-4">
                <FaRocket className="text-cyan-600 dark:text-cyan-400 text-2xl" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                DevOps & CI/CD
              </h3>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              I integrate testing into pipelines with{" "}
              <span className="text-cyan-600 dark:text-cyan-400 font-medium">GitHub Actions</span>:
              install, test, generate HTML reports, and upload artifacts for traceability.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              I value repeatability, early failure signals, and useful artifacts that speed up debugging.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs px-2 py-1 rounded border bg-gray-50 text-cyan-700 border-cyan-100 dark:bg-gray-700 dark:text-cyan-300 dark:border-gray-600">
                GitHub Actions
              </span>
              <span className="text-xs px-2 py-1 rounded border bg-gray-50 text-cyan-700 border-cyan-100 dark:bg-gray-700 dark:text-cyan-300 dark:border-gray-600">
                CI/CD
              </span>
              <span className="text-xs px-2 py-1 rounded border bg-gray-50 text-cyan-700 border-cyan-100 dark:bg-gray-700 dark:text-cyan-300 dark:border-gray-600">
                Reports & Artifacts
              </span>
            </div>
          </motion.div>

          {/* Full‑Stack Development */}
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay: 0.2 }}
            className="rounded-xl p-6 md:p-8 border
                       bg-white/70 border-gray-200 shadow-sm
                       dark:bg-gray-800/50 dark:border-gray-700
                       hover:border-cyan-400/60 hover:shadow-cyan-500/10 transition-all duration-300"
          >
            <div className="flex items-center mb-5 md:mb-6">
              <div className="bg-cyan-500/15 dark:bg-cyan-500/20 p-3 rounded-full mr-4">
                <FaCode className="text-cyan-600 dark:text-cyan-400 text-2xl" />
              </div>
              <h3 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white">
                Full‑Stack Development
              </h3>
            </div>
            <p className="text-gray-700 dark:text-gray-300 mb-4">
              I build user‑focused apps with{" "}
              <span className="text-cyan-600 dark:text-cyan-400 font-medium">React</span>,{" "}
              <span className="text-cyan-600 dark:text-cyan-400 font-medium">Laravel</span>, and{" "}
              <span className="text-cyan-600 dark:text-cyan-400 font-medium">Express</span>.
            </p>
            <p className="text-gray-700 dark:text-gray-300">
              Projects like <span className="text-cyan-600 dark:text-cyan-400">WEGYM</span> and{" "}
              <span className="text-cyan-600 dark:text-cyan-400">EduMatch</span> show my ability to ship
              robust, maintainable features end‑to‑end.
            </p>
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-xs px-2 py-1 rounded border bg-gray-50 text-cyan-700 border-cyan-100 dark:bg-gray-700 dark:text-cyan-300 dark:border-gray-600">
                React
              </span>
              <span className="text-xs px-2 py-1 rounded border bg-gray-50 text-cyan-700 border-cyan-100 dark:bg-gray-700 dark:text-cyan-300 dark:border-gray-600">
                Laravel
              </span>
              <span className="text-xs px-2 py-1 rounded border bg-gray-50 text-cyan-700 border-cyan-100 dark:bg-gray-700 dark:text-cyan-300 dark:border-gray-600">
                Express
              </span>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ delay: 0.2, duration: 0.4 }}
          className="mt-16 md:mt-20 text-center"
        >
          <p className="text-lg md:text-xl text-gray-700 dark:text-gray-300 mb-6">
            Want to discuss QA automation, DevOps pipelines, or a full‑stack project?
          </p>
          <Link
            to="contact"
            smooth
            duration={500}
            className="inline-block cursor-pointer bg-gradient-to-r from-cyan-500 to-blue-600 px-8 py-3 rounded-lg font-medium text-white
                       hover:shadow-lg hover:shadow-cyan-500/20 active:scale-[0.98] transition-all duration-300"
          >
            Let's Connect
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default About;